/**
 * Clip Lifetime Page
 * Macro-level overview of a clip's entire recorded history, with site-level grouping
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClipDataStore } from '../stores/clipDataStore';
import { useDiaperStore } from '../stores/diaperStore';
import { useLifetimeStore } from '../stores/lifetimeStore';
import { useSessionsStore } from '../stores/sessionsStore';
import { ClipFileSelector } from '../components/monitor/ClipFileSelector';
import { format, parseISO } from 'date-fns';
import { ParentSize } from '@visx/responsive';
import { Group } from '@visx/group';
import { scaleTime, scaleBand, scaleLinear } from '@visx/scale';
import { AxisLeft } from '@visx/axis';
import { useTooltip, TooltipWithBounds, defaultStyles } from '@visx/tooltip';
import { localPoint } from '@visx/event';
import { BackendDiaper } from '../lib/api-clip-monitor';
import { getModelColor, INVALID_MODEL_COLOR } from '../lib/modelColors';

const tooltipStyles = {
  ...defaultStyles,
  backgroundColor: 'rgba(0, 0, 0, 0.9)',
  color: 'white',
  padding: '10px 14px',
  borderRadius: '6px',
  fontSize: '12px',
  lineHeight: '1.5',
  pointerEvents: 'none' as const,
  zIndex: 100,
};

interface MetaDiaperBlock {
  type: 'meta_diaper';
  clipAddress: string;
  diaper: BackendDiaper;
  startTime: Date;
  endTime: Date;
  durationMs: number;
  anomalies: { flag: boolean; reasons: string[] };
}

interface RawDiaperBlock {
  type: 'raw_diaper';
  clipAddress: string;
  counter: number;
  modelCode: number;
  startTime: Date;
  endTime: Date;
  durationMs: number;
  recordCount: number;
  annotationsCount: number;
}

interface GapBlock {
  type: 'gap';
  clipAddress: string;
  startTime: Date;
  endTime: Date;
  durationMs: number;
  reason: 'Sleep Mode' | 'Powered Down' | 'Detached' | 'Unknown';
}

type TimelineBlock = MetaDiaperBlock | RawDiaperBlock | GapBlock;

function hasAnomalies(diaper: BackendDiaper): { flag: boolean; reasons: string[] } {
  const reasons: string[] = [];
  if (diaper.alerts.leak_time || diaper.alerts.leak_alert_time) reasons.push('Leak Alert');
  if (diaper.alerts.pool_time || diaper.alerts.pool_alert_time) reasons.push('Pool Alert');
  if (diaper.model_code === 0) reasons.push('Unknown Model (0)');
  if (diaper.model_code === 31) reasons.push('All Shorted (31)');
  
  if (diaper.start_time && diaper.end_time) {
    const start = parseISO(diaper.start_time).getTime();
    const end = parseISO(diaper.end_time).getTime();
    const durationMs = end - start;
    
    if (durationMs < 15 * 60 * 1000) reasons.push('Very short duration (<15m)');
    if (durationMs > 18 * 60 * 60 * 1000) reasons.push('Very long duration (>18h)');
  }
  
  if (diaper.closing_percent > 90) reasons.push('High volume (>90%)');
  
  return { flag: reasons.length > 0, reasons };
}

const formatDuration = (ms: number) => {
  if (ms < 0) return '0s';
  const d = Math.floor(ms / (1000 * 60 * 60 * 24));
  const h = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const m = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  const parts = [];
  if (d > 0) parts.push(`${d}d`);
  if (h > 0) parts.push(`${h}h`);
  if (m > 0 || parts.length === 0) parts.push(`${m}m`);
  return parts.join(' ');
};

export const ClipLifetimePage: React.FC = () => {
  const navigate = useNavigate();
  const {
    serverOnline,
    selectedClipAddress,
    clipData,
    annotations,
    checkServer,
    loadClipData,
    loadAnnotations,
    backendMeta: singleClipMeta,
    activeSourceId,
    activeBucket,
  } = useClipDataStore();
  const { diaperModels, getDiaperModel, loadDiaperModels } = useDiaperStore();
  const currentNovaAccountId = useSessionsStore((s) => s.currentNovaAccountId);

  const {
    allMetas, allWetnessSamples, loadingMetas, loadingWetness,
    viewMode, setViewMode, selectedSite, setSelectedSite,
    sortCol, setSortCol, sortDesc, setSortDesc,
    fetchMetas, fetchWetness,
  } = useLifetimeStore();

  const {
    showTooltip,
    hideTooltip,
    tooltipOpen,
    tooltipData,
    tooltipLeft = 0,
    tooltipTop = 0,
  } = useTooltip<TimelineBlock>();

  // Legend highlight: hover a legend item → highlight all matching blocks
  const [highlightedModel, setHighlightedModel] = useState<number | 'invalid' | null>(null);

  const isModelHighlighted = useCallback((code: number): boolean | null => {
    if (highlightedModel === null) return null; // nothing hovered
    if (highlightedModel === 'invalid') return !getDiaperModel(code)?.name;
    return code === highlightedModel;
  }, [highlightedModel, getDiaperModel]);

  useEffect(() => {
    checkServer();
  }, [checkServer]);

  useEffect(() => {
    if (!currentNovaAccountId) return;
    loadDiaperModels(currentNovaAccountId).catch(console.error);
  }, [currentNovaAccountId, loadDiaperModels]);

  // Fetch metas when server comes online, or source/bucket changes (cached in store)
  useEffect(() => {
    if (!serverOnline) return;
    const cacheKey = `${activeSourceId}:${activeBucket}`;
    fetchMetas(cacheKey);
  }, [serverOnline, activeSourceId, activeBucket, fetchMetas]);

  // Make sure we load the data if a clip is selected and no meta exists (for fallback)
  useEffect(() => {
    if (selectedClipAddress && clipData.length === 0) {
      loadClipData();
    }
    if (selectedClipAddress) {
      loadAnnotations();
    }
  }, [selectedClipAddress, clipData.length, loadClipData, loadAnnotations]);

  const availableSites = useMemo(() => {
    return Array.from(new Set(Object.values(allMetas).map(m => m.site_name).filter(Boolean))).sort();
  }, [allMetas]);

  // Derived state for the timeline blocks
  const { blocksByClip, globalStats, xScale, yScale, clipsList } = useMemo(() => {
    const computedBlocksByClip: Record<string, TimelineBlock[]> = {};
    let minTime = new Date('2099-01-01');
    let maxTime = new Date('2000-01-01');
    
    let activeClips: string[] = [];
    
    // CASE A: Site Overview Mode
    if (viewMode === 'site' && selectedSite) {
      const siteMetas = Object.values(allMetas).filter(m => m.site_name === selectedSite);
      activeClips = siteMetas.map(m => m.clip_address);
      
      siteMetas.forEach(meta => {
        const blocks: TimelineBlock[] = [];
        meta.diapers.forEach(d => {
          if (!d.start_time || !d.end_time) return;
          const start = parseISO(d.start_time);
          const end = parseISO(d.end_time);
          if (start < minTime) minTime = start;
          if (end > maxTime) maxTime = end;
          blocks.push({
            type: 'meta_diaper',
            clipAddress: meta.clip_address,
            diaper: d,
            startTime: start,
            endTime: end,
            durationMs: end.getTime() - start.getTime(),
            anomalies: hasAnomalies(d)
          });
        });
        
        // Sort blocks and compute gaps
        blocks.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
        const withGaps: TimelineBlock[] = [];
        for (let i = 0; i < blocks.length; i++) {
          withGaps.push(blocks[i]);
          if (i < blocks.length - 1) {
            const gapStart = blocks[i].endTime;
            const gapEnd = blocks[i+1].startTime;
            const gapMs = gapEnd.getTime() - gapStart.getTime();
            if (gapMs > 60000) {
              withGaps.push({
                type: 'gap',
                clipAddress: meta.clip_address,
                startTime: gapStart,
                endTime: gapEnd,
                durationMs: gapMs,
                reason: 'Unknown'
              });
            }
          }
        }
        computedBlocksByClip[meta.clip_address] = withGaps;
      });
    } 
    // CASE B: Single Clip Mode
    else if (viewMode === 'clip' && selectedClipAddress) {
      activeClips = [selectedClipAddress];
      const meta = allMetas[selectedClipAddress] || singleClipMeta;
      
      if (meta && meta.diapers.length > 0) {
        // We have metadata for this clip
        const blocks: TimelineBlock[] = [];
        meta.diapers.forEach(d => {
          if (!d.start_time || !d.end_time) return;
          const start = parseISO(d.start_time);
          const end = parseISO(d.end_time);
          if (start < minTime) minTime = start;
          if (end > maxTime) maxTime = end;
          blocks.push({
            type: 'meta_diaper',
            clipAddress: meta.clip_address,
            diaper: d,
            startTime: start,
            endTime: end,
            durationMs: end.getTime() - start.getTime(),
            anomalies: hasAnomalies(d)
          });
        });
        
        blocks.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
        const withGaps: TimelineBlock[] = [];
        for (let i = 0; i < blocks.length; i++) {
          withGaps.push(blocks[i]);
          if (i < blocks.length - 1) {
            const gapStart = blocks[i].endTime;
            const gapEnd = blocks[i+1].startTime;
            const gapMs = gapEnd.getTime() - gapStart.getTime();
            if (gapMs > 60000) {
              withGaps.push({
                type: 'gap',
                clipAddress: meta.clip_address,
                startTime: gapStart,
                endTime: gapEnd,
                durationMs: gapMs,
                reason: 'Unknown'
              });
            }
          }
        }
        computedBlocksByClip[selectedClipAddress] = withGaps;
      } else if (clipData && clipData.length > 0) {
        // Fallback to raw data grouping
        const blocks: TimelineBlock[] = [];
        let currentDiaper: RawDiaperBlock | null = null;
        let lastRecordTime: Date | null = null;
        let lastStatusCode: number | null = null;

        for (const record of clipData) {
          const ts = new Date(record.timestamp);
          const counter = record.parsed.diaperCounter;
          const modelCode = record.parsed.diaperModel;
          const status = record.parsed.statusCode;
          
          if (ts < minTime) minTime = ts;
          if (ts > maxTime) maxTime = ts;

          if (!currentDiaper || currentDiaper.counter !== counter) {
            if (currentDiaper) {
              currentDiaper.durationMs = currentDiaper.endTime.getTime() - currentDiaper.startTime.getTime();
              blocks.push(currentDiaper);
              
              if (lastRecordTime) {
                const gapMs = ts.getTime() - lastRecordTime.getTime();
                if (gapMs > 60000) {
                  let reason: GapBlock['reason'] = 'Unknown';
                  if (lastStatusCode === 1) reason = 'Detached';
                  else if (lastStatusCode === 9) reason = 'Sleep Mode';
                  else if (lastStatusCode === 255 || lastStatusCode === -1) reason = 'Powered Down';
                  
                  blocks.push({
                    type: 'gap',
                    clipAddress: selectedClipAddress,
                    startTime: lastRecordTime,
                    endTime: ts,
                    durationMs: gapMs,
                    reason,
                  });
                }
              }
            }

            currentDiaper = {
              type: 'raw_diaper',
              clipAddress: selectedClipAddress,
              counter,
              modelCode,
              startTime: ts,
              endTime: ts,
              durationMs: 0,
              recordCount: 1,
              annotationsCount: annotations.filter(a => a.diaperCounter === counter).length,
            };
          } else {
            currentDiaper.endTime = ts;
            currentDiaper.recordCount++;
            if (currentDiaper.modelCode === 0 && modelCode !== 0) {
              currentDiaper.modelCode = modelCode;
            }
          }

          lastRecordTime = ts;
          lastStatusCode = status;
        }

        if (currentDiaper) {
          currentDiaper.durationMs = currentDiaper.endTime.getTime() - currentDiaper.startTime.getTime();
          blocks.push(currentDiaper);
        }

        blocks.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
        computedBlocksByClip[selectedClipAddress] = blocks;
      }
    }

    if (activeClips.length === 0 || minTime > maxTime) {
      return { blocksByClip: {}, globalStats: null, xScale: null, yScale: null, clipsList: [] };
    }

    // Pad time domain slightly
    // Add extra padding at the start so the first day label isn't clipped
    const domainStart = new Date(minTime);
    domainStart.setHours(0, 0, 0, 0); // Snap to start of day
    
    const domainEnd = new Date(maxTime);
    domainEnd.setHours(23, 59, 59, 999); // Snap to end of day

    const scaleX = scaleTime<number>({ domain: [domainStart, domainEnd] });
    
    // Sort clips so they are consistent in the Y axis
    const sortedClips = activeClips.sort((a, b) => {
      const nameA = allMetas[a]?.clip_name || a;
      const nameB = allMetas[b]?.clip_name || b;
      return nameA.localeCompare(nameB);
    });
    
    const scaleY = scaleBand<string>({ domain: sortedClips, padding: 0.1 });

    // Compute stats
    let totalDiapers = 0;
    let totalAlerts = 0;
    let totalMs = 0;
    Object.values(computedBlocksByClip).forEach(blocks => {
      blocks.forEach(b => {
        if (b.type === 'meta_diaper' || b.type === 'raw_diaper') {
          totalDiapers++;
          totalMs += b.durationMs;
        }
        if (b.type === 'meta_diaper') {
          if (b.anomalies.flag) totalAlerts++;
        }
      });
    });

    return {
      blocksByClip: computedBlocksByClip,
      clipsList: sortedClips,
      globalStats: {
        diaperCount: totalDiapers,
        alertCount: totalAlerts,
        avgDurationMs: totalDiapers > 0 ? totalMs / totalDiapers : 0,
        startTime: minTime,
        endTime: maxTime,
      },
      xScale: scaleX,
      yScale: scaleY,
    };
  }, [viewMode, selectedSite, selectedClipAddress, allMetas, singleClipMeta, clipData, annotations]);

  // Fetch wetness samples only when timeline is visible - for the clips we're actually showing (cached)
  useEffect(() => {
    if (!serverOnline || clipsList.length === 0) return;
    fetchWetness(clipsList);
  }, [serverOnline, clipsList, fetchWetness]);

  // Aggregate all diapers for the table
  const allTableDiapers = useMemo(() => {
    const list: MetaDiaperBlock[] = [];
    Object.values(blocksByClip).forEach(blocks => {
      blocks.forEach(b => {
        if (b.type === 'meta_diaper') {
          list.push(b);
        }
      });
    });
    
    return list.sort((a, b) => {
      let cmp = 0;
      if (sortCol === 'start') {
        cmp = a.startTime.getTime() - b.startTime.getTime();
      } else if (sortCol === 'duration') {
        cmp = a.durationMs - b.durationMs;
      } else if (sortCol === 'volume') {
        cmp = a.diaper.closing_volume - b.diaper.closing_volume;
      } else if (sortCol === 'clip') {
        const nameA = allMetas[a.clipAddress]?.clip_name || a.clipAddress;
        const nameB = allMetas[b.clipAddress]?.clip_name || b.clipAddress;
        cmp = nameA.localeCompare(nameB);
      }
      return sortDesc ? -cmp : cmp;
    });
  }, [blocksByClip, sortCol, sortDesc, allMetas]);

  const handleBlockClick = (block: TimelineBlock) => {
    if (block.type === 'meta_diaper') {
      const url = `/diaper-inspector?clip=${encodeURIComponent(block.clipAddress)}&diaperStart=${encodeURIComponent(block.diaper.start_time)}`;
      console.log('[Inspector] ClipLifetime: navigate to Inspector (meta_diaper)', { clip: block.clipAddress, diaperStart: block.diaper.start_time, url });
      navigate(url);
    } else if (block.type === 'raw_diaper') {
      const url = `/diaper-inspector?clip=${encodeURIComponent(block.clipAddress)}&diaper=${block.counter}`;
      console.log('[Inspector] ClipLifetime: navigate to Inspector (raw_diaper)', { clip: block.clipAddress, diaper: block.counter, url });
      navigate(url);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">📅 Clip Lifetime Overview</h2>
          <div className="flex bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => setViewMode('site')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${viewMode === 'site' ? 'bg-white shadow text-blue-600' : 'text-gray-600 hover:bg-gray-200'}`}
            >
              Site Overview
            </button>
            <button
              onClick={() => setViewMode('clip')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${viewMode === 'clip' ? 'bg-white shadow text-blue-600' : 'text-gray-600 hover:bg-gray-200'}`}
            >
              Single Clip
            </button>
          </div>
        </div>
        
      </div>

      <ClipFileSelector hideClipSelect={viewMode === 'site'} />

      {viewMode === 'site' && (
        <div className="bg-white rounded-lg shadow-sm px-4 py-3 flex items-center gap-4">
          <label className="font-semibold text-gray-700 text-sm">Filter by Site:</label>
          <select
            value={selectedSite || ''}
            onChange={(e) => setSelectedSite(e.target.value)}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
            disabled={loadingMetas}
          >
            {availableSites.length === 0 && <option value="">{loadingMetas ? 'Loading...' : 'No metadata available'}</option>}
            {availableSites.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <span className="text-xs text-gray-400">
            {Object.keys(allMetas).length} clips with metadata
          </span>
          {loadingMetas && (
            <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          )}
        </div>
      )}

      {globalStats && xScale && yScale && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
              <p className="text-sm text-gray-500 font-medium">Total Diapers</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">{globalStats.diaperCount}</p>
              {viewMode === 'site' && <p className="text-xs text-gray-400 mt-1">Across {clipsList.length} clips</p>}
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
              <p className="text-sm text-gray-500 font-medium">Flagged Anomalies</p>
              <p className="text-2xl font-bold text-red-600 mt-1">{globalStats.alertCount}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
              <p className="text-sm text-gray-500 font-medium">Avg Wear Time</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">{formatDuration(globalStats.avgDurationMs)}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
              <p className="text-sm text-gray-500 font-medium">Time Range</p>
              <p className="text-sm font-bold text-gray-800 mt-1">{format(globalStats.startTime, 'MMM d, yyyy')}</p>
              <p className="text-sm font-bold text-gray-800">to {format(globalStats.endTime, 'MMM d, yyyy')}</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <h3 className="text-lg font-bold">Timeline</h3>
              {loadingWetness && (
                <div className="flex items-center gap-2 text-xs text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                  <svg className="animate-spin h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Loading wetness curves...
                </div>
              )}
            </div>
            
            <div className="w-full relative" style={{ height: Math.max(150, clipsList.length * 80 + 60) }}>
              <ParentSize>
                {({ width, height }) => {
                  if (width === 0 || height === 0) return null;

                  const margin = { top: 28, right: 20, bottom: 30, left: viewMode === 'site' ? 120 : 20 };
                  const innerWidth = width - margin.left - margin.right;
                  const innerHeight = height - margin.top - margin.bottom;

                  xScale.range([0, innerWidth]);
                  yScale.range([0, innerHeight]);

                  const handleMouseMove = (e: React.MouseEvent, block: TimelineBlock) => {
                    const point = localPoint(e);
                    if (!point) return;
                    showTooltip({
                      tooltipData: block,
                      tooltipLeft: point.x,
                      tooltipTop: point.y,
                    });
                  };

                  // Generate midnight markers, day labels, and daytime blocks
                  const midnights: Date[] = [];
                  const dayLabels: { x: number, text: string }[] = [];
                  const dayBlocks: {start: number, end: number}[] = [];
                  
                  const domain = xScale.domain();
                  const startMidnight = new Date(domain[0]);
                  startMidnight.setHours(0, 0, 0, 0);
                  if (startMidnight < domain[0]) {
                    startMidnight.setDate(startMidnight.getDate() + 1);
                  }
                  
                  // For shading, we need to go back before the domain start if needed
                  const shadeStart = new Date(domain[0]);
                  shadeStart.setHours(0, 0, 0, 0);
                  
                  for (let d = new Date(shadeStart); d <= domain[1]; d.setDate(d.getDate() + 1)) {
                    if (d >= startMidnight) {
                      midnights.push(new Date(d));
                    }
                    
                    const dayStart = new Date(d);
                    dayStart.setHours(6, 0, 0, 0);
                    
                    const dayEnd = new Date(d);
                    dayEnd.setHours(22, 0, 0, 0);
                    
                    const startX = xScale(dayStart) || 0;
                    const endX = xScale(dayEnd) || 0;
                    
                    if (endX > 0 && startX < innerWidth) {
                      dayBlocks.push({
                        start: Math.max(0, startX),
                        end: Math.min(innerWidth, endX)
                      });
                    }
                    
                    // Day label in the middle of the day (noon)
                    const noon = new Date(d);
                    noon.setHours(12, 0, 0, 0);
                    const noonX = xScale(noon) || 0;
                    if (noonX >= 0 && noonX <= innerWidth) {
                      dayLabels.push({ x: noonX, text: format(d, 'MMM d') });
                    }
                  }

                  return (
                    <>
                      <svg width={width} height={height}>
                        <defs>
                          <pattern id="hatch-no-telemetry" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
                            <line x1="0" y1="0" x2="0" y2="6" stroke="rgba(255,255,255,0.7)" strokeWidth="3" />
                          </pattern>
                        </defs>
                        <style>{`
                          @keyframes legend-pulse {
                            0%, 100% { opacity: 1; }
                            50% { opacity: 0.6; }
                          }
                          .legend-pulse { animation: legend-pulse 0.8s ease-in-out infinite; }
                        `}</style>
                        <Group left={margin.left} top={margin.top}>
                          {/* Nighttime background (default) */}
                          <rect x={0} y={0} width={innerWidth} height={innerHeight} fill="#f8fafc" />
                          
                          {/* Daytime background blocks */}
                          {dayBlocks.map((block, i) => (
                            <rect 
                              key={`day-${i}`}
                              x={block.start}
                              y={0}
                              width={block.end - block.start}
                              height={innerHeight}
                              fill="#fffdeb"
                            />
                          ))}

                          {/* Day boundary markers (background layer) */}
                          {midnights.map((midnight, i) => {
                            const x = xScale(midnight) || 0;
                            return (
                              <line
                                key={`midnight-${i}`}
                                x1={x}
                                y1={0}
                                x2={x}
                                y2={innerHeight}
                                stroke="#cbd5e1"
                                strokeWidth={1}
                              />
                            );
                          })}
                          
                          {/* Day Labels (centered between midnight lines, at bottom) */}
                          {dayLabels.map((lbl, i) => (
                            <text
                              key={`daylabel-${i}`}
                              x={lbl.x}
                              y={innerHeight + 18}
                              fill="#475569"
                              fontSize={12}
                              fontWeight={600}
                              textAnchor="middle"
                            >
                              {lbl.text}
                            </text>
                          ))}

                          {clipsList.map((clipAddr) => {
                            const y = yScale(clipAddr) || 0;
                            const rowHeight = yScale.bandwidth();
                            const blocks = blocksByClip[clipAddr] || [];
                            
                            return (
                              <g key={clipAddr}>
                                {/* Background track */}
                                <rect
                                  x={0}
                                  y={y}
                                  width={innerWidth}
                                  height={rowHeight}
                                  fill="#f3f4f6"
                                  rx={4}
                                />
                                
                                {/* Blocks */}
                                {blocks.map((block, i) => {
                                  const x1 = Number(xScale(block.startTime) ?? 0);
                                  const x2 = Number(xScale(block.endTime) ?? 0);
                                  const w = Math.max(2, x2 - x1);
                                  
                                  if (block.type === 'gap') {
                                    return (
                                      <rect
                                        key={`gap-${i}`}
                                        x={x1}
                                        y={y + rowHeight * 0.3}
                                        width={w}
                                        height={rowHeight * 0.4}
                                        fill="#d1d5db"
                                        onMouseMove={(e) => handleMouseMove(e, block)}
                                        onMouseLeave={hideTooltip}
                                      />
                                    );
                                  }

                                  const isMeta = block.type === 'meta_diaper';
                                  const modelCode = isMeta ? block.diaper.model_code : block.modelCode;
                                  const color = getModelColor(modelCode);
                                  const hasAlert = isMeta && block.anomalies.flag;
                                  const fillPercent = isMeta ? block.diaper.closing_percent : 0;
                                  
                                  // Wetness sparkline: filter by time range only
                                  const samples = allWetnessSamples[clipAddr] || [];
                                  const blockStartMs = block.startTime.getTime();
                                  const blockEndMs = block.endTime.getTime();
                                  const blockSamples = samples.filter(s => {
                                    const t = new Date(s.time).getTime();
                                    return t >= blockStartMs && t <= blockEndMs;
                                  });

                                  const sparklineYScale = scaleLinear<number>({
                                    domain: [0, 15000], // Typical max wetness
                                    range: [y + rowHeight - 2, y + 2] // slight padding top/bottom
                                  });

                                  let sparklinePath = '';
                                  if (blockSamples.length > 0) {
                                    sparklinePath = blockSamples.map((s, idx) => {
                                      const sx = Number(xScale(new Date(s.time)) ?? 0);
                                      const sy = sparklineYScale(s.wetnessLevel) ?? (y + rowHeight);
                                      return `${idx === 0 ? 'M' : 'L'} ${sx} ${sy}`;
                                    }).join(' ');
                                  }
                                  
                                  const clipPathId = `clip-${clipAddr}-${i}`;

                                  return (
                                    <g key={`diaper-${i}`}>
                                      <defs>
                                        <clipPath id={clipPathId}>
                                          <rect x={x1} y={y} width={w} height={rowHeight} rx={3} />
                                        </clipPath>
                                      </defs>
                                      
                                      {/* Main diaper block */}
                                      {(() => {
                                        const hl = isModelHighlighted(modelCode);
                                        const baseOpacity = hl === null ? 0.85 : hl ? 1 : 0.12;
                                        return (
                                          <rect
                                            x={x1}
                                            y={y}
                                            width={w}
                                            height={rowHeight}
                                            fill={color}
                                            rx={3}
                                            stroke={hl ? 'white' : hasAlert ? '#ef4444' : 'none'}
                                            strokeWidth={hl ? 2 : hasAlert ? 2 : 0}
                                            strokeDasharray={hasAlert && !hl ? "4,2" : "none"}
                                            opacity={baseOpacity}
                                            className={`cursor-pointer transition-opacity ${hl ? 'legend-pulse' : 'hover:opacity-100'}`}
                                            onClick={() => handleBlockClick(block)}
                                            onMouseMove={(e) => handleMouseMove(e, block)}
                                            onMouseLeave={hideTooltip}
                                          />
                                        );
                                      })()}

                                      {/* Wetness sparkline */}
                                      {sparklinePath && w > 5 && (
                                        <path
                                          d={sparklinePath}
                                          fill="none"
                                          stroke="rgba(255,255,255,0.6)"
                                          strokeWidth={1.5}
                                          clipPath={`url(#${clipPathId})`}
                                          className="pointer-events-none"
                                        />
                                      )}
                                      
                                      {/* Volume fill indicator (bottom of the block) */}
                                      {isMeta && fillPercent > 0 && w > 5 && (
                                        <rect
                                          x={x1}
                                          y={y + rowHeight - 4}
                                          width={w * Math.min(1, fillPercent / 100)}
                                          height={4}
                                          fill="rgba(0,0,0,0.3)"
                                          rx={2}
                                          className="pointer-events-none"
                                        />
                                      )}

                                      {/* Alert indicators */}
                                      {isMeta && w > 10 && (
                                        <g className="pointer-events-none">
                                          {block.diaper.alerts.leak_time && (
                                            <circle cx={x1 + w/2} cy={y + 8} r={3} fill="#ef4444" /> // Red dot for leak
                                          )}
                                          {block.diaper.alerts.pool_time && !block.diaper.alerts.leak_time && (
                                            <circle cx={x1 + w/2} cy={y + 8} r={3} fill="#f59e0b" /> // Orange dot for pool
                                          )}
                                          {block.diaper.alerts.wet_time && !block.diaper.alerts.pool_time && !block.diaper.alerts.leak_time && (
                                            <circle cx={x1 + w/2} cy={y + 8} r={3} fill="#3b82f6" /> // Blue dot for wet
                                          )}
                                        </g>
                                      )}
                                      
                                      {/* Emiction count */}
                                      {isMeta && block.diaper.emictions.length > 0 && w > 15 && (
                                        <text
                                          x={x1 + w/2}
                                          y={y + rowHeight/2 + 2}
                                          textAnchor="middle"
                                          dominantBaseline="middle"
                                          fill="white"
                                          fontSize={9}
                                          fontWeight="bold"
                                          className="pointer-events-none"
                                        >
                                          {block.diaper.emictions.length}
                                        </text>
                                      )}
                                    </g>
                                  );
                                })}
                              </g>
                            );
                          })}

                          {/* Telemetry coverage dimming — hatch pattern on parts of diaper blocks outside telemetry range */}
                          {clipsList.map((clipAddr) => {
                            const y = yScale(clipAddr) || 0;
                            const rowHeight = yScale.bandwidth();
                            const blocks = blocksByClip[clipAddr] || [];
                            const samples = allWetnessSamples[clipAddr] || [];
                            if (samples.length === 0) return null;

                            const sampleTimes = samples.map(s => new Date(s.time).getTime());
                            const clipTelemetryStart = Math.min(...sampleTimes);
                            const clipTelemetryEnd = Math.max(...sampleTimes);

                            return (
                              <g key={`dim-${clipAddr}`} className="pointer-events-none">
                                {blocks.map((block, i) => {
                                  if (block.type === 'gap') return null;
                                  const blockStartMs = block.startTime.getTime();
                                  const blockEndMs = block.endTime.getTime();

                                  const dimBefore = blockStartMs < clipTelemetryStart && clipTelemetryStart < blockEndMs;
                                  const dimAfter = blockEndMs > clipTelemetryEnd && clipTelemetryEnd > blockStartMs;
                                  const dimEntire = blockEndMs <= clipTelemetryStart || blockStartMs >= clipTelemetryEnd;

                                  if (dimEntire) {
                                    const x1 = Number(xScale(block.startTime) ?? 0);
                                    const x2 = Number(xScale(block.endTime) ?? 0);
                                    return (
                                      <rect key={`dim-full-${i}`} x={x1} y={y} width={Math.max(1, x2 - x1)} height={rowHeight} fill="url(#hatch-no-telemetry)" rx={3} />
                                    );
                                  }

                                  return (
                                    <g key={`dim-parts-${i}`}>
                                      {dimBefore && (() => {
                                        const x1 = Number(xScale(block.startTime) ?? 0);
                                        const x2 = Number(xScale(new Date(clipTelemetryStart)) ?? 0);
                                        const w = Math.max(0, x2 - x1);
                                        return w > 0 ? <rect x={x1} y={y} width={w} height={rowHeight} fill="url(#hatch-no-telemetry)" rx={3} /> : null;
                                      })()}
                                      {dimAfter && (() => {
                                        const x1 = Number(xScale(new Date(clipTelemetryEnd)) ?? 0);
                                        const x2 = Number(xScale(block.endTime) ?? 0);
                                        const w = Math.max(0, x2 - x1);
                                        return w > 0 ? <rect x={x1} y={y} width={w} height={rowHeight} fill="url(#hatch-no-telemetry)" rx={3} /> : null;
                                      })()}
                                    </g>
                                  );
                                })}
                              </g>
                            );
                          })}

                          {/* Midnight lines overlay — rendered on top of diaper blocks */}
                          {midnights.map((midnight, i) => {
                            const x = xScale(midnight) || 0;
                            return (
                              <g key={`midnight-overlay-${i}`} className="pointer-events-none">
                                <line
                                  x1={x}
                                  y1={0}
                                  x2={x}
                                  y2={innerHeight}
                                  stroke="#475569"
                                  strokeWidth={1}
                                  strokeDasharray="8,4"
                                />
                                <text
                                  x={x}
                                  y={-6}
                                  fill="#475569"
                                  fontSize={9}
                                  fontWeight={600}
                                  textAnchor="middle"
                                >
                                  {format(midnight, 'MMM d')}
                                </text>
                              </g>
                            );
                          })}

                          {viewMode === 'site' && (
                            <AxisLeft
                              scale={yScale}
                              stroke="none"
                              tickStroke="none"
                              numTicks={clipsList.length}
                              tickValues={clipsList}
                              tickFormat={(val) => {
                                const meta = allMetas[val];
                                if (!meta) {
                                  // No metadata — extract last segment of MAC address
                                  const parts = val.split(':');
                                  return parts.length >= 2 ? parts.slice(-2).join(':') : val;
                                }
                                const name = meta.clip_name || '';
                                // Try to extract a short identifier like "A8105" from names like "s-Heeren-Loo A8105"
                                const idMatch = name.match(/\b([A-Z]\d{3,5})\b/);
                                if (idMatch) return idMatch[1];
                                return name.length > 16 ? `${name.substring(0, 14)}..` : name || val.substring(val.length - 5);
                              }}
                              tickLabelProps={() => ({
                                fill: '#4b5563',
                                fontSize: 11,
                                fontWeight: 500,
                                textAnchor: 'end',
                                dy: '0.33em',
                                dx: '-8px'
                              })}
                            />
                          )}

                          {/* Bottom axis: just a line, no ticks — dates are shown as centered day labels above */}
                          <line x1={0} y1={innerHeight} x2={innerWidth} y2={innerHeight} stroke="#d1d5db" />
                        </Group>
                      </svg>

                      {tooltipData && tooltipOpen && (
                        <TooltipWithBounds top={tooltipTop} left={tooltipLeft} style={tooltipStyles}>
                          {tooltipData.type === 'meta_diaper' ? (
                            <div>
                              <div className="font-bold mb-1 border-b border-gray-600 pb-1 text-blue-300">
                                🩲 Diaper #{tooltipData.diaper.diaper_id}
                                <span className="text-gray-400 text-xs ml-2 font-normal">
                                  ({allMetas[tooltipData.clipAddress]?.clip_name || tooltipData.clipAddress.substring(12)})
                                </span>
                              </div>
                              <div>
                                <strong>Model:</strong> {getDiaperModel(tooltipData.diaper.model_code)?.name || `Unknown (${tooltipData.diaper.model_code})`}
                              </div>
                              <div>
                                <strong>Time:</strong> {format(tooltipData.startTime, 'MMM d, HH:mm')} - {format(tooltipData.endTime, 'HH:mm')}
                              </div>
                              <div>
                                <strong>Duration:</strong> {formatDuration(tooltipData.durationMs)}
                              </div>
                              <div className="mt-1">
                                <strong>Volume:</strong> {tooltipData.diaper.closing_volume}ml ({tooltipData.diaper.closing_percent}%)
                              </div>
                              <div>
                                <strong>Pees:</strong> {tooltipData.diaper.emictions.length}
                              </div>
                              {tooltipData.anomalies.flag && (
                                <div className="mt-2 pt-1 border-t border-red-500 text-red-400">
                                  ⚠️ <strong>Alerts:</strong> {tooltipData.anomalies.reasons.join(', ')}
                                </div>
                              )}
                              <div className="text-gray-400 text-[10px] mt-2 italic">
                                Click to inspect diaper
                              </div>
                            </div>
                          ) : tooltipData.type === 'raw_diaper' ? (
                            <div>
                              <div className="font-bold mb-1 border-b border-gray-600 pb-1 text-gray-300">
                                🩲 Diaper #{tooltipData.counter} (Raw Data)
                              </div>
                              <div>
                                <strong>Model:</strong> {getDiaperModel(tooltipData.modelCode)?.name || `Unknown (${tooltipData.modelCode})`}
                              </div>
                              <div>
                                <strong>Duration:</strong> {formatDuration(tooltipData.durationMs)}
                              </div>
                              <div>
                                <strong>Records:</strong> {tooltipData.recordCount}
                              </div>
                              <div className="text-gray-400 text-[10px] mt-2 italic">
                                No metadata available. Click to inspect.
                              </div>
                            </div>
                          ) : (
                            <div>
                              <div className="font-bold mb-1 border-b border-gray-600 pb-1 text-gray-300">
                                ⏸ Gap Period
                              </div>
                              <div><strong>Reason:</strong> {tooltipData.reason}</div>
                              <div><strong>Duration:</strong> {formatDuration(tooltipData.durationMs)}</div>
                            </div>
                          )}
                        </TooltipWithBounds>
                      )}
                    </>
                  );
                }}
              </ParentSize>
            </div>

            {/* Legend */}
            <div className="mt-4 flex flex-col gap-3 text-sm border-t border-gray-100 pt-4">
              <div className="flex flex-wrap gap-x-6 gap-y-2 justify-center">
                {(() => {
                  const allCodes = Array.from(new Set(Object.values(blocksByClip).flatMap(blocks => blocks.filter(b => b.type === 'meta_diaper' || b.type === 'raw_diaper').map(b => b.type === 'meta_diaper' ? b.diaper.model_code : (b as RawDiaperBlock).modelCode))));
                  const named = allCodes.filter(c => c !== 31 && c !== 0 && getDiaperModel(c)?.name).sort((a, b) => a - b);
                  const hasInvalid = allCodes.some(c => c === 0 || c === 31 || !getDiaperModel(c)?.name);
                  return (
                    <>
                      {named.map(code => (
                        <div
                          key={code}
                          className="flex items-center gap-2 cursor-pointer select-none rounded px-1.5 py-0.5 transition-colors hover:bg-gray-100"
                          onMouseEnter={() => setHighlightedModel(code)}
                          onMouseLeave={() => setHighlightedModel(null)}
                        >
                          <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: getModelColor(code) }}></div>
                          <span className="text-gray-600 text-xs">{getDiaperModel(code)!.name}</span>
                        </div>
                      ))}
                      {hasInvalid && (
                        <div
                          className="flex items-center gap-2 cursor-pointer select-none rounded px-1.5 py-0.5 transition-colors hover:bg-gray-100"
                          onMouseEnter={() => setHighlightedModel('invalid')}
                          onMouseLeave={() => setHighlightedModel(null)}
                        >
                          <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: INVALID_MODEL_COLOR }}></div>
                          <span className="text-gray-600 text-xs">Unknown / Invalid</span>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-2 justify-center">
                <div className="flex items-center gap-2">
                  <svg width="12" height="12"><circle cx="6" cy="6" r="4" fill="#ef4444"/></svg>
                  <span className="text-gray-600">Leak Alert</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg width="12" height="12"><circle cx="6" cy="6" r="4" fill="#f59e0b"/></svg>
                  <span className="text-gray-600">Pool Alert</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-sm border-2 border-dashed border-red-500 bg-gray-200"></div>
                  <span className="text-gray-600">Anomaly Flagged</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-gray-500 rounded-sm"></div>
                  <span className="text-gray-600">Black bar inside = Volume %</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg width="24" height="12">
                    <path d="M0 10 Q 6 2, 12 8 T 24 0" fill="none" stroke="#9ca3af" strokeWidth="1.5" />
                  </svg>
                  <span className="text-gray-600">White line = Wetness curve</span>
                </div>
              </div>
            </div>
          </div>

          {/* Diaper Table */}
          {allTableDiapers.length > 0 && (
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-bold">Diaper Summary ({allTableDiapers.length})</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="text-xs text-gray-700 bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 cursor-pointer hover:bg-gray-100" onClick={() => { setSortCol('clip'); setSortDesc(!sortDesc); }}>
                        Clip {sortCol === 'clip' ? (sortDesc ? '↓' : '↑') : ''}
                      </th>
                      <th className="px-4 py-3">Diaper #</th>
                      <th className="px-4 py-3">Model</th>
                      <th className="px-4 py-3 cursor-pointer hover:bg-gray-100" onClick={() => { setSortCol('start'); setSortDesc(!sortDesc); }}>
                        Start Time {sortCol === 'start' ? (sortDesc ? '↓' : '↑') : ''}
                      </th>
                      <th className="px-4 py-3 cursor-pointer hover:bg-gray-100" onClick={() => { setSortCol('duration'); setSortDesc(!sortDesc); }}>
                        Duration {sortCol === 'duration' ? (sortDesc ? '↓' : '↑') : ''}
                      </th>
                      <th className="px-4 py-3 cursor-pointer hover:bg-gray-100" onClick={() => { setSortCol('volume'); setSortDesc(!sortDesc); }}>
                        Volume {sortCol === 'volume' ? (sortDesc ? '↓' : '↑') : ''}
                      </th>
                      <th className="px-4 py-3">Alerts / Issues</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allTableDiapers.map((block, i) => {
                      const rowHl = isModelHighlighted(block.diaper.model_code);
                      return (
                      <tr 
                        key={`${block.clipAddress}-${block.diaper.diaper_id}-${i}`}
                        className={`border-b cursor-pointer transition-all ${rowHl === true ? 'ring-1 ring-inset ring-blue-400' : ''} ${rowHl === false ? 'opacity-20' : 'hover:bg-blue-50'} ${block.anomalies.flag && rowHl !== false ? 'bg-red-50' : ''}`}
                        style={rowHl === true ? { backgroundColor: getModelColor(block.diaper.model_code) + '12' } : undefined}
                        onClick={() => handleBlockClick(block)}
                      >
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {allMetas[block.clipAddress]?.clip_name || block.clipAddress.substring(12)}
                        </td>
                        <td className="px-4 py-3">{block.diaper.diaper_id}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: getModelColor(block.diaper.model_code) }}></div>
                            {getDiaperModel(block.diaper.model_code)?.name || `Unknown (${block.diaper.model_code})`}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">{format(block.startTime, 'MMM d, HH:mm')}</td>
                        <td className="px-4 py-3">{formatDuration(block.durationMs)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span>{block.diaper.closing_volume}ml</span>
                            <span className="text-xs text-gray-500">({block.diaper.closing_percent}%)</span>
                            {block.diaper.emictions.length > 0 && (
                              <span className="text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded" title="Pee events">
                                💧 {block.diaper.emictions.length}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-red-600 font-medium text-xs">
                          {block.anomalies.reasons.join(', ')}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {(!globalStats || (!selectedSite && viewMode === 'site' && !loadingMetas) || (!selectedClipAddress && viewMode === 'clip')) && (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <p className="text-6xl mb-4">📅</p>
          <p className="text-xl font-semibold text-gray-700 mb-2">
            {viewMode === 'site' ? 'Select a site to view overview' : 'Select a clip to view lifetime'}
          </p>
        </div>
      )}
    </div>
  );
};
